import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const PriceSchema = z.object({
  currency: z.string(),
  value: z.number().positive("Price must be greater than 0"),
});

const TrialSchema = z.object({
  durationDays: z.number().int().positive(),
  fixedPrice: z.number().min(0),
});

const BillingPeriodSchema = z.enum([
  "MONTHLY",
  "QUARTERLY",
  "ANNUAL",
  "BIANNUAL",
  "BIENNIAL",
  "WEEKLY",
  "BIWEEKLY",
  "DAILY",
  "THIRTY_DAYS",
  "SIXTY_DAYS",
  "NINETY_DAYS",
]);

const EntryStatusSchema = z.enum(["DRAFT", "ACTIVE", "RETIRED"]);

// Kill Bill types <plan name>/<product name> as xs:ID (NCName): an identifier
// must start with a letter (or underscore for products) and can never begin with
// a digit, or Kill Bill rejects the whole catalog with an opaque "[2080] Invalid
// catalog for tenant". Plans are lowercase kebab-case; products allow TitleCase.
const KB_PLAN_ID = /^[a-z][a-z0-9-]*$/;
const KB_PRODUCT_ID = /^[A-Za-z_][A-Za-z0-9_-]*$/;
const PLAN_ID_MSG = "must be kebab-case and start with a letter (no leading digit)";
const PRODUCT_ID_MSG =
  "productName must start with a letter or underscore (no leading digit); use productDisplayName for free-form labels";

// ─── DTOs ─────────────────────────────────────────────────────────────────────
// Schemas are annotated with explicit z.ZodType<T> to short-circuit deep type
// inference inside the ts-rest router (otherwise tsc emits TS2589 when generating
// declaration files).

export interface CatalogEntryPrice {
  currency: string;
  value: number;
}

export interface CatalogTrial {
  durationDays: number;
  fixedPrice: number;
}

type EntryStatus = "DRAFT" | "ACTIVE" | "RETIRED";

export interface CatalogPlan {
  id: string;
  planName: string;
  displayName?: string;
  billingPeriod: string;
  status: EntryStatus;
  recurringPrices: CatalogEntryPrice[];
  trial?: CatalogTrial;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogAddOn {
  id: string;
  addOnName: string;
  displayName?: string;
  billingPeriod: string;
  status: EntryStatus;
  recurringPrices: CatalogEntryPrice[];
  trial?: CatalogTrial;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogProduct {
  id: string;
  name: string;
  displayName?: string;
  status: EntryStatus;
  plans: CatalogPlan[];
  addOns: CatalogAddOn[];
}

const CatalogPlanDto: z.ZodType<CatalogPlan> = z.object({
  id: z.string(),
  planName: z.string(),
  displayName: z.string().optional(),
  billingPeriod: z.string(),
  status: EntryStatusSchema,
  recurringPrices: z.array(z.object({ currency: z.string(), value: z.number() })),
  trial: TrialSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const CatalogAddOnDto: z.ZodType<CatalogAddOn> = z.object({
  id: z.string(),
  addOnName: z.string(),
  displayName: z.string().optional(),
  billingPeriod: z.string(),
  status: EntryStatusSchema,
  recurringPrices: z.array(z.object({ currency: z.string(), value: z.number() })),
  trial: TrialSchema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const CatalogProductDto: z.ZodType<CatalogProduct> = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().optional(),
  status: EntryStatusSchema,
  plans: z.array(CatalogPlanDto),
  addOns: z.array(CatalogAddOnDto),
});

// ─── Inputs ───────────────────────────────────────────────────────────────────

const CreatePlanInput = z.object({
  planName: z.string().regex(KB_PLAN_ID, `planName ${PLAN_ID_MSG}`),
  productName: z.string().min(1).regex(KB_PRODUCT_ID, PRODUCT_ID_MSG),
  displayName: z.string().optional(),
  productDisplayName: z.string().optional(),
  billingPeriod: BillingPeriodSchema,
  recurringPrices: z.array(PriceSchema).min(1),
  trial: TrialSchema.optional(),
});

const UpdatePlanInput = z.object({
  displayName: z.string().optional(),
  recurringPrices: z.array(PriceSchema).min(1).optional(),
  // trial: undefined → leave existing trial alone; null → remove trial; object → set/update.
  trial: TrialSchema.nullable().optional(),
  productDisplayName: z.string().optional(),
});

const ClonePlanInput = z.object({
  newPlanName: z.string().regex(KB_PLAN_ID, `newPlanName ${PLAN_ID_MSG}`),
  newDisplayName: z.string().optional(),
  recurringPrices: z.array(PriceSchema).min(1).optional(),
  // trial: undefined → copy source trial; null → clone without trial; object → set cloned trial.
  trial: TrialSchema.nullable().optional(),
});

const CreateAddOnInput = z.object({
  addOnName: z.string().regex(KB_PLAN_ID, `addOnName ${PLAN_ID_MSG}`),
  productName: z.string().min(1).regex(KB_PRODUCT_ID, PRODUCT_ID_MSG),
  displayName: z.string().optional(),
  productDisplayName: z.string().optional(),
  billingPeriod: BillingPeriodSchema,
  recurringPrices: z.array(PriceSchema).min(1),
  trial: TrialSchema.optional(),
});

const UpdateAddOnInput = z.object({
  displayName: z.string().optional(),
  recurringPrices: z.array(PriceSchema).min(1).optional(),
  productDisplayName: z.string().optional(),
});

// ─── Publish state ────────────────────────────────────────────────────────────

const PublishStatusDto = z.discriminatedUnion("state", [
  z.object({ state: z.literal("draft"), unpublishedCount: z.number() }),
  z.object({ state: z.literal("publishing") }),
  z.object({
    state: z.literal("published"),
    kbEffectiveDate: z.string().optional().nullable(),
    unpublishedCount: z.number(),
  }),
  z.object({
    state: z.literal("failed"),
    kbErrorCode: z.number().optional().nullable(),
    kbErrorMessage: z.string().optional().nullable(),
    unpublishedCount: z.number(),
  }),
]);

// ─── Version history ──────────────────────────────────────────────────────────

const CatalogVersionDto = z.object({
  effectiveDate: z.string(),
});

const errorSchema = z.object({ message: z.string() });

// ─── Contract ─────────────────────────────────────────────────────────────────

export const catalogContract = c.router({
  // Products (reads Prisma)
  listProducts: {
    method: "GET",
    path: "/billing/catalog/products",
    responses: {
      200: z.lazy(() => z.array(CatalogProductDto)) as z.ZodType<CatalogProduct[]>,
      401: errorSchema,
      500: errorSchema,
    },
    summary: "List all products with their plans and add-ons",
  },

  // Plans
  getPlan: {
    method: "GET",
    path: "/billing/catalog/plans/:planName",
    pathParams: z.object({ planName: z.string() }),
    responses: {
      200: z.object({ plan: CatalogPlanDto, product: CatalogProductDto.optional() }) as z.ZodType<{
        plan: CatalogPlan;
        product?: CatalogProduct;
      }>,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Get a single plan",
  },

  createPlan: {
    method: "POST",
    path: "/billing/catalog/plans",
    body: CreatePlanInput,
    responses: {
      201: CatalogPlanDto,
      400: errorSchema,
      401: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Create a new plan and publish catalog",
  },

  updatePlan: {
    method: "PUT",
    path: "/billing/catalog/plans/:planName",
    pathParams: z.object({ planName: z.string() }),
    body: UpdatePlanInput,
    responses: {
      200: CatalogPlanDto,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Update a plan and publish catalog",
  },

  retirePlan: {
    method: "POST",
    path: "/billing/catalog/plans/:planName/retire",
    pathParams: z.object({ planName: z.string() }),
    body: z.object({}),
    responses: {
      200: z.object({ planName: z.string(), retiredAt: z.string() }),
      401: errorSchema,
      404: errorSchema,
      409: z.object({ message: z.string(), subscriberCount: z.number() }),
      500: errorSchema,
    },
    summary: "Retire a plan (mark unavailable for new subscriptions)",
  },

  restorePlan: {
    method: "POST",
    path: "/billing/catalog/plans/:planName/restore",
    pathParams: z.object({ planName: z.string() }),
    body: z.object({}),
    responses: {
      200: CatalogPlanDto,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Restore a retired plan",
  },

  clonePlan: {
    method: "POST",
    path: "/billing/catalog/plans/:planName/clone",
    pathParams: z.object({ planName: z.string() }),
    body: ClonePlanInput,
    responses: {
      201: CatalogPlanDto,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Clone an existing plan under a new name",
  },

  // Add-ons
  listAddOns: {
    method: "GET",
    path: "/billing/catalog/addons",
    responses: {
      200: z.array(CatalogAddOnDto) as z.ZodType<CatalogAddOn[]>,
      401: errorSchema,
      500: errorSchema,
    },
    summary: "List all add-ons (flattened across products)",
  },

  createAddOn: {
    method: "POST",
    path: "/billing/catalog/addons",
    body: CreateAddOnInput,
    responses: {
      201: CatalogAddOnDto,
      400: errorSchema,
      401: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Create a new add-on and publish catalog",
  },

  updateAddOn: {
    method: "PUT",
    path: "/billing/catalog/addons/:addOnName",
    pathParams: z.object({ addOnName: z.string() }),
    body: UpdateAddOnInput,
    responses: {
      200: CatalogAddOnDto,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Update an add-on and publish catalog",
  },

  retireAddOn: {
    method: "POST",
    path: "/billing/catalog/addons/:addOnName/retire",
    pathParams: z.object({ addOnName: z.string() }),
    body: z.object({}),
    responses: {
      200: z.object({ addOnName: z.string(), retiredAt: z.string() }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Retire an add-on",
  },

  restoreAddOn: {
    method: "POST",
    path: "/billing/catalog/addons/:addOnName/restore",
    pathParams: z.object({ addOnName: z.string() }),
    body: z.object({}),
    responses: {
      200: CatalogAddOnDto,
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Restore a retired add-on",
  },

  // Publish state
  getPublishStatus: {
    method: "GET",
    path: "/billing/catalog/publish-status",
    responses: {
      200: PublishStatusDto,
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Get the current catalog publish state",
  },

  retryPublish: {
    method: "POST",
    path: "/billing/catalog/retry-publish",
    body: z.object({}),
    responses: {
      200: z.object({ ok: z.boolean() }),
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Retry the catalog publish without mutating Prisma",
  },

  discardDraft: {
    method: "POST",
    path: "/billing/catalog/discard-draft",
    body: z.object({}),
    responses: {
      200: z.object({ discarded: z.boolean() }),
      401: errorSchema,
      500: errorSchema,
    },
    summary:
      "Discard unpublished edits by reverting the working draft to the catalog live in Kill Bill. Does not delete the catalog; published versions are immutable.",
  },

  syncFromKillBill: {
    method: "POST",
    path: "/billing/catalog/sync-from-killbill",
    body: z.object({}),
    responses: {
      200: z.object({
        productCount: z.number(),
        planCount: z.number(),
        addOnCount: z.number(),
      }),
      401: errorSchema,
      500: errorSchema,
    },
    summary:
      "Rebuild the editable catalog from the version live in Kill Bill (recovery / resync). Display names are restored from plan/add-on metadata.",
  },

  // Version history (reads KB)
  listCatalogVersions: {
    method: "GET",
    path: "/billing/catalog/versions",
    responses: {
      200: z.array(CatalogVersionDto),
      401: errorSchema,
      500: errorSchema,
    },
    summary: "List Kill Bill catalog versions (version history)",
  },

  getCatalogVersion: {
    method: "GET",
    path: "/billing/catalog/versions/xml",
    query: z.object({ requestedDate: z.string().optional() }),
    responses: {
      200: z.string(),
      401: errorSchema,
      500: errorSchema,
    },
    summary: "Get Kill Bill catalog XML for a specific version (diagnostics)",
  },
});
